import { useMemo } from "react";
import { useTranslations } from "next-intl";
import { useEnumeration } from "@/api/enums/enumsApi";

export type ActionTypeMode = 'actions' | 'transcribe' | 'evaluation';

/**
 * Хук для получения имени типа действия
 * Унифицирует логику для всех ReportCard и ChartBlock компонентов
 * 
 * @param mode - тип отчета (actions, transcribe, evaluation)
 * @returns функция для получения имени типа действия
 * 
 * @example
 * ```tsx
 * const getActionTypeName = useActionTypeName('actions');
 * getActionTypeName('copy_paste'); // "Копирование/Вставка"
 * ```
 */
export function useActionTypeName(mode: ActionTypeMode = 'actions') {
  const t = useTranslations();
  const { data: actionTypes } = useEnumeration("AssignmentActionType");

  // Маппинг для actions (из API + локальные переводы для environment events)
  const actionTypeMap = useMemo<Record<string, string>>(() => {
    const baseMap = actionTypes && actionTypes.length > 0
      ? Object.fromEntries(actionTypes.map((item) => [item.raw, item.name]))
      : {};

    // Добавляем локальные переводы для environment events (с бэкенда не приходят)
    return {
      env_lost_focus: t("label-env-lost-focus"),
      env_gained_focus: t("label-env-gained-focus"),
      env_change_resolution: t("label-env-change-resolution"),
      ...baseMap,
    } as Record<string, string>;
  }, [actionTypes, t]);

  // Маппинг для transcribe (локальный)
  const transcribeTypeMap = useMemo<Record<string, string>>(() => ({
    multiple_speakers_detected: t("label-transcribe-multiple-speakers"),
    dialogue_pattern: t("label-transcribe-dialogue-pattern"),
    imperative_commands: t("label-transcribe-imperative-commands"),
    technical_discussion: t("label-transcribe-technical-discussion"),
    coordination_attempts: t("label-transcribe-coordination-attempts"),
  }), [t]);

  // Маппинг для evaluation (локальный)
  const evaluationTypeMap = useMemo<Record<string, string>>(() => ({
    actions: t("label-actions-report"),
    answers: t("label-answers-report"),
    transcribe: t("label-transcribe-report"),
    q_density: t("label-answers-report"),
  }), [t]);

  // Возвращаем функцию для получения имени
  return useMemo(() => {
    return (actionType: string): string => {
      switch (mode) {
        case 'transcribe':
          return transcribeTypeMap[actionType] || actionType;
        case 'evaluation':
          return evaluationTypeMap[actionType] || actionType;
        case 'actions':
        default:
          return actionTypeMap[actionType] || actionType;
      }
    };
  }, [mode, actionTypeMap, transcribeTypeMap, evaluationTypeMap]);
}
